import React, { useMemo } from 'react';
import { PhoneIncoming, PhoneOutgoing, ChevronRight } from 'lucide-react';
import { Button, Chip, Table, type TableColumn } from 'common/components';
import type { Lead } from '../CallDetailsModal/CallDetailsModal';
import { formatCallOutcome, formatLeadStatus, getLeadStatusChipColor, getOutcomeChipColor } from '../../utils';

import styles from './LiveMonitorTable.module.scss';

export interface LiveMonitorTableProps {
    leads: Lead[];
    selectedCall: Lead | null;
    isLoading: boolean;
    onSelectCall: (lead: Lead) => void;
}

const getDirectionLabel = (callDirection?: string): string => {
    const value = callDirection?.toUpperCase();
    if (value?.includes('INBOUND')) return 'Inbound';
    if (value?.includes('OUTBOUND')) return 'Outbound';
    if (value?.includes('MANUAL')) return 'Manual';
    return '—';
};

export const LiveMonitorTable: React.FC<LiveMonitorTableProps> = ({ leads, selectedCall, isLoading, onSelectCall }) => {
    const columns: TableColumn<Lead>[] = useMemo(
        () => [
            {
                key: 'callTime',
                title: 'Time',
                width: 100,
                render: (_, record) => (
                    <div>
                        <div className={styles.primaryText}>{record.callTime}</div>
                        <div className={styles.secondaryText}>{record.callDate}</div>
                    </div>
                ),
            },
            {
                key: 'callDirection',
                title: 'Direction',
                width: 110,
                render: (_, record) => {
                    const label = getDirectionLabel(record.callDirection);
                    return (
                        <span className={styles.directionCell}>
                            {label === 'Inbound' ? <PhoneIncoming size={14} className={styles.inboundIcon} /> : null}
                            {label === 'Outbound' || label === 'Manual' ? (
                                <PhoneOutgoing size={14} className={styles.outboundIcon} />
                            ) : null}
                            {label}
                        </span>
                    );
                },
            },
            {
                key: 'outcome',
                title: 'Status',
                width: 140,
                render: (_, record) => (
                    <Chip variant='filled' corner='pill' color={getOutcomeChipColor(record.outcome)} size='sm'>
                        {formatCallOutcome(record.outcome)}
                    </Chip>
                ),
            },
            {
                key: 'partyStatus',
                title: 'Party',
                width: 130,
                render: (_, record) => (
                    <Chip variant='filled' corner='pill' color={getLeadStatusChipColor(record.crmStatus)} size='sm'>
                        {formatLeadStatus(record.crmStatus)}
                    </Chip>
                ),
            },
            {
                key: 'name',
                title: 'Name',
                dataIndex: 'name',
                width: 140,
            },
            {
                key: 'duration',
                title: 'Duration',
                width: 90,
                render: (_, record) => <span title={record.durationTooltip || ''}>{record.duration}</span>,
            },
            {
                key: 'select',
                title: '',
                width: 56,
                align: 'right',
                render: (_, record) => (
                    <Button
                        variant='ghost'
                        size='sm'
                        iconOnly
                        icon={ChevronRight}
                        aria-label={`Open details for ${record.name}`}
                        onClick={(event) => {
                            event.stopPropagation();
                            onSelectCall(record);
                        }}
                    />
                ),
            },
        ],
        [onSelectCall]
    );

    return (
        <div className={styles.tableWrap} data-testid='live-monitor-table'>
            <Table<Lead>
                data={leads}
                columns={columns}
                emptyText='No active calls right now.'
                rowKey={(record) => record.callId || record.id}
                onRowClick={onSelectCall}
                rowClassName={(record) => (selectedCall?.callId === record.callId ? styles.selectedRow : styles.row)}
                showHeader
                variant='secondary'
                hoverable
                size='medium'
                isLoading={isLoading}
            />
        </div>
    );
};
